import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Grid, Typography, Box, Button, IconButton, TextField, FormControl, FormHelperText } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/DeleteSharp";
import MUIAutocomplete from "@material-ui/lab/Autocomplete";
import { Link } from "react-router-dom";
import pick from "lodash/pick";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import { makeStyles } from "@material-ui/core/styles";
import countries from "iso-3166-country-list";
import Alert from "@material-ui/lab/Alert";
import { useHistory, useParams } from "react-router-dom";
import { Input, Select, Checkbox, Autocomplete, CustomMultiSelect } from "components/Shared/mui-formik-inputs";
import { CustomerSchema } from "constants/validation-schemas";
import { CustomerFormAllowedFields } from "constants/forms-submit-allowed-fields";
import { PATHS } from "util/appConstants";
import SelectGeoCoordinates from "components/Customers/SelectGeoCoordinates";
import FormCancelSaveButton from "components/Shared/FormCancelSaveButtons";
import { OpeningHoursEditor } from "components/Shared/OpeningHours";
import { isAdminUserSelector, selectUser } from "redux/slices/userSlice";
import { getCustomers } from "redux/slices/customerSlice";
import { useDidUpdateEffect } from "../../util/use-did-update-effect";
import { usePermission } from "../../hooks/usePermission";
import { getSuppliers, selectSuppliers } from "redux/slices/supplierSlice";

const useStyles = makeStyles({
  _heading: {
    font: "normal normal normal 28px/40px Questrial",
    color: "#121212",
  },
  _icons: {
    color: "#ADADAD",
    cursor: "pointer",
    transition: "all 0.3s ease-in-out",
    margin: "0 16px 0 0px",
    fontSize: "35px",
  },
  _save: {
    "&:hover": {
      transform: "scale(1.3)",
      color: "#6F9CEB",
    },
    "&:hover + span": {
      display: "block",
    },
  },
  _close: {
    "&:hover": {
      transform: "scale(1.3)",
      color: "#525252",
    },
    "&:hover + span": {
      display: "block",
    },
  },
  _subheading: {
    font: "normal normal 500 22px/32px Roboto",
    color: " #121212",
    marginTop: "44px",
    marginBottom: "40px",
  },
  _dflex: {
    display: "flex",
    alignItems: "center",
  },
});

const CustomerForm = ({ initialValues, onSubmit, action, tourList, zipcodes }) => {
  const [similarCustomers, setSimilarCustomers] = React.useState([]);
  const [isManualAddress, setIsManualAddress] = React.useState(false);
  const [newDepositFiles, setNewDepositFiles] = React.useState([]);
  const [existingDepositFiles, setExistingDepositFiles] = React.useState(initialValues?.deposit_agreement_files || []);
  const [fileError, setFileError] = React.useState(null);
  const { t, i18n } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const suppliers = useSelector(selectSuppliers);
  const { id } = useParams();

  React.useEffect(() => {
    if (initialValues?.deposit_agreement_files) {
      setExistingDepositFiles(initialValues.deposit_agreement_files);
    }
  }, [initialValues?.deposit_agreement_files]);

  const isAdminUser = useSelector(isAdminUserSelector);
  const getTourTypeCategory = (tour) => tour?.tour_type_category ?? tour?.tour_category;
  const deliveryTours = (tourList || []).filter((tour) => getTourTypeCategory(tour) !== "pickup");
  const isPickupTour = (tour) => getTourTypeCategory(tour) === "pickup";

  const handleDepositAgreementFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const allowedExtensions = /\.(pdf|jpe?g|png|webp|gif)$/i;
    const allowedMimeTypes = ["application/pdf", "image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif"];

    const validFiles = [];
    for (const file of files) {
      if (!allowedMimeTypes.includes(file.type) && !file.name.match(allowedExtensions)) {
        setFileError(t("Only PDF and Image files are allowed"));
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setFileError(t("Upload file size should not exceed 5MB"));
        return;
      }
      validFiles.push(file);
    }

    setFileError(null);
    setNewDepositFiles((prev) => [...prev, ...validFiles]);
    e.target.value = "";
  };

  const handleRemoveExistingFile = (indexToRemove) => {
    setExistingDepositFiles((prev) => prev.filter((_, i) => i !== indexToRemove));
  };

  const handleRemoveNewFile = (indexToRemove) => {
    setNewDepositFiles((prev) => prev.filter((_, i) => i !== indexToRemove));
  };

  const validateAddress = (values) => {
    const errors = {};

    const missingFields = [];
    if (!values.street_number) missingFields.push(t("house number"));
    if (!values.zipcode) missingFields.push(t("zipcode"));
    if (!values.city) missingFields.push(t("city"));
    if (!values.country) missingFields.push(t("country"));

    if (missingFields.length > 0) {
      errors.address = t(
        "Invalid address. Please provide and select the full address with {{missingFields}}.",
        { missingFields: missingFields.join(", ") }
      );
      return errors;
    }

    if (values.zipcode && values.country) {
      const zipcodePattern = /^[A-Z0-9\s\-]{1,15}$/i;

      if (!zipcodePattern.test(values.zipcode)) {
        errors.address = t(
          "Invalid zipcode format. Please enter a valid zipcode (1-15 alphanumeric characters, spaces, and dashes allowed). Current value: {{current}}",
          {
            current: values.zipcode
          }
        );
      }
    }

    if (values.latitude && values.longitude) {
      const lat = parseFloat(values.latitude);
      const lng = parseFloat(values.longitude);

      if (isNaN(lat) || isNaN(lng)) {
        errors.address = t("Invalid coordinates. Please select a valid address from the map.");
      } else if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        errors.address = t("Invalid coordinates. Please select a valid address from the map.");
      }
    }

    if (values.street_number && !/^[A-Z0-9\s\-/]+$/i.test(values.street_number)) {
      errors.address = t("Invalid house number format. Please enter a valid house number.");
    }

    return errors;
  };

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: true,
    validationSchema: CustomerSchema,
    validate: validateAddress,
    initialValues: {
      number: "",
      contact_salutation: "",
      contact_name: "",
      contact_surname: "",
      email_notifications: false,
      tour: "",
      position: "",
      keybox_code: "",
      tour_mappings: [{ tour_id: null, departures: [] }],
      name: "", // required
      street: "", // required
      street_number: "", // required
      city: "", // required
      zipcode: "", // required
      country: "", // required
      email: "", // required
      phone: "", // required
      active: true,
      default_remarks: initialValues?.default_remarks || initialValues?.remarks || "",
      ...initialValues,
      deposit_agreement:
        initialValues?.deposit_agreement_text ||
        (initialValues?.deposit_agreement &&
        initialValues?.deposit_agreement !== "NONE" &&
        initialValues?.deposit_agreement !== "BRING_KEY" &&
        initialValues?.deposit_agreement !== "KEY_BOX"
          ? initialValues.deposit_agreement
          : initialValues?.deposit_agreement === "KEY_BOX" && initialValues?.keybox_code
          ? `Keybox Code: ${initialValues.keybox_code}`
          : ""),
      deposit_agreement_files: initialValues?.deposit_agreement_files || [],
      existing_deposit_agreement_files: initialValues?.deposit_agreement_files || [],
      opening_hours:
        initialValues?.opening_timing ||
        initialValues?.opening_hours ||
        initialValues?.opening_timings ||
        null,
      duplicate: initialValues?.duplicate ?? false, // Archive is treated as duplicate
      latitude: initialValues?.latitude || 0, // required
      longitude: initialValues?.longitude || 0, // required
      tour_automatic_assigned: !(initialValues?.tour_manually_assigned ?? false),
      priority: initialValues?.priority ?? false,
      supplier_id: initialValues?.supplier_id ?? null,
      formatted_address: initialValues?.formatted_address ?? null,
    },
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payloadData = pick(
          {
            ...values,
            tour_manually_assigned: !values.tour_automatic_assigned,
            opening_timing: values.opening_hours || values.opening_timing || null,
          },
          CustomerFormAllowedFields
        );

        const hasNewFiles = newDepositFiles.length > 0;
        const initialFilesCount = initialValues?.deposit_agreement_files?.length || 0;
        const existingChanged = existingDepositFiles.length !== initialFilesCount;

        if (hasNewFiles || existingChanged) {
          const formData = new FormData();
          Object.keys(payloadData).forEach((key) => {
            if (key === "deposit_agreement_files" || key === "existing_deposit_agreement_files") return;
            if (payloadData[key] !== null && payloadData[key] !== undefined) {
              if (typeof payloadData[key] === "object" && !(payloadData[key] instanceof File)) {
                formData.append(key, JSON.stringify(payloadData[key]));
              } else {
                formData.append(key, payloadData[key]);
              }
            }
          });

          formData.append("existing_deposit_agreement_files", JSON.stringify(existingDepositFiles));
          for (const file of newDepositFiles) {
            formData.append("deposit_agreement_files", file);
          }

          await onSubmit(formData);
        } else {
          await onSubmit({
            ...payloadData,
            existing_deposit_agreement_files: existingDepositFiles,
          });
        }
      } catch (err) {
        setSubmitting(false);
      }
    },
  });
  const {
    values,
    handleChange,
    setFieldValue,
    errors,
    handleSubmit,
    submitCount,
    isValid,
    isSubmitting,
    setFieldTouched,
  } = formik;
  const applyZipcodeTour = (zipcodeOrList) => {
    if (zipcodeOrList && !Array.isArray(zipcodeOrList) && zipcodeOrList.tour_mappings?.length) {
      setFieldValue("tour_mappings", zipcodeOrList.tour_mappings);
      return true;
    }

    const rows = Array.isArray(zipcodeOrList)
      ? zipcodeOrList
      : zipcodeOrList
        ? [zipcodeOrList]
        : [];

    if (rows.length > 0) {
      const tourMap = new Map();
      for (const r of rows) {
        const timings = r.ZipcodeDeliveryTimings || [];
        for (const zdt of timings) {
          const tdt = zdt.ToursDeliveryTiming;
          if (tdt) {
            const tid = Number(tdt.tour_id);
            if (!tourMap.has(tid)) {
              tourMap.set(tid, new Set());
            }
            if (tdt.delivery_timing) {
              tourMap.get(tid).add(tdt.delivery_timing);
            }
          }
        }
        const zipcodeTour = r.Tour;
        if (zipcodeTour && !isPickupTour(zipcodeTour)) {
          const tid = Number(zipcodeTour.id);
          if (!tourMap.has(tid)) {
            tourMap.set(tid, new Set());
          }
          const tourDeps = (zipcodeTour.delivery_timing_configs || [])
            .map((c) => c.delivery_timing)
            .filter(Boolean);
          for (const dep of tourDeps) {
            tourMap.get(tid).add(dep);
          }
        }
      }

      const tourMappings = [...tourMap.entries()].map(([tid, deps]) => ({
        tour_id: tid,
        departures: [...deps],
      }));

      if (tourMappings.length > 0) {
        setFieldValue("tour_mappings", tourMappings);
        return true;
      }
    }
    return false;
  };

  const applyDefaultDeliveryTour = () => {
    const def = deliveryTours.find((t) => t.is_default) || deliveryTours[0];
    if (def) {
      setFieldValue("tour_mappings", [{
        tour_id: def.id,
        departures: (def.delivery_timing_configs || []).map(c => c.delivery_timing).filter(Boolean)
      }]);
    }
  };

  const applyAutomaticTourFromPostal = (postalValue, formSupplierIdOverride) => {
    if (!values.tour_automatic_assigned) return;
    const formSupplier =
      formSupplierIdOverride !== undefined ? formSupplierIdOverride : values.supplier_id;
    let supplierId;
    if (isAdminUser) {
      const v = formSupplier;
      if (v != null && v !== "") {
        supplierId = Number(v);
      } else {
        supplierId = user?.supplier_id != null ? Number(user.supplier_id) : null;
      }
    } else {
      supplierId = user?.supplier_id != null ? Number(user.supplier_id) : null;
    }
    if (supplierId == null) {
      applyDefaultDeliveryTour();
      return;
    }
    const needle = (postalValue || "").trim().toLowerCase();
    if (!needle) return;
    const matchingRows = zipcodes.filter(
      (zc) =>
        zc.zipcode?.trim().toLowerCase() === needle && Number(zc.supplier_id) === supplierId
    );
    if (matchingRows.length > 0) {
      if (!applyZipcodeTour(matchingRows)) applyDefaultDeliveryTour();
    } else {
      applyDefaultDeliveryTour();
    }
  };

  let { handleBlur } = formik;

  const isAutoGeoLocation = values?.geo_location_status === 'auto' || values?.geo_location_status === 'error';
  if (!submitCount) {
    handleBlur = null;
  }

  const checkDuplicated = () => {
    if (values.zipcode && values.street_number && !initialValues?.id) {
      dispatch(
        getCustomers(false, {
          zipcode: values.zipcode,
          street_number: values.street_number,
        })
      ).then((results) => {
        const uniqueCustomers = results.reduce((acc, customer) => {
          if (!acc.has(customer.name)) {
            acc.set(customer.name, customer);
          }
          return acc;
        }, new Map());
        const uniqueCustomersArray = Array.from(uniqueCustomers.values());
        uniqueCustomersArray.forEach((uniqueCustomer) => {
          results.forEach((customer) => {
            if (customer.supplier_id === user.supplier_id) {
              if (customer.name === uniqueCustomer.name) {
                uniqueCustomer.id = customer.id;
              }
            }
          });
        });
        setSimilarCustomers(uniqueCustomersArray);
      });
    }
  };

  const customHandleChange = (e) => {
    const { value } = e.target;
    applyAutomaticTourFromPostal(value);
  };

  const closeCustomerHandler = () => {
    action === "ADD" ? history.push(PATHS.customers.root) : history.push(PATHS.customers.detail.replace(":id", id));
  };

  useDidUpdateEffect(() => {
    applyAutomaticTourFromPostal(values.zipcode);
  }, [values.tour_automatic_assigned, values.supplier_id]);

  useEffect(() => {
    if (!values.tour_automatic_assigned || !zipcodes?.length) return;
    if (!(values.zipcode || "").trim()) return;
    applyAutomaticTourFromPostal(values.zipcode);
  }, [zipcodes]);

  const showLocationPerm = usePermission("customers.show_location_fields");
  const showLocationFields = !usePermission("customersHideLocationRelatedFields") && !!showLocationPerm;

  const cancelButtonRef = useRef(null);
  const saveButtonRef = useRef(null);

  const lastElementRef = useRef(null);

  useEffect(() => {
    const handleTabPress = (e) => {
      if (e.key === "Tab") {
        if (lastElementRef.current && lastElementRef.current.contains(document.activeElement)) {
          e.preventDefault();
          if (cancelButtonRef.current) {
            window.scrollTo({ top: 0, behavior: "smooth" });
            cancelButtonRef.current.focus();
          }
        }
      }
    };

    document.addEventListener("keydown", handleTabPress);
    return () => {
      document.removeEventListener("keydown", handleTabPress);
    };
  }, []);

  useEffect(() => {
    dispatch(getSuppliers());
  }, [dispatch]);

  useEffect(() => {
    if ((values.zipcode || values.street_number) && !initialValues?.id) {
      checkDuplicated();
    }
  }, [values.zipcode, values.street_number]);

  const reEvaluateAddress = () => {
    if (errors.street_number || errors.zipcode || errors.city || errors.country) {
      const isAddressValid = values.street_number && values.zipcode && values.city && values.country;
      if (isAddressValid) {
        setFieldTouched("street_number", false);
        setFieldTouched("zipcode", false);
        setFieldTouched("city", false);
        setFieldTouched("country", false);
        setFieldTouched("tour_mappings", false);
      }
    }
  };

  useEffect(() => {
    reEvaluateAddress();
  }, [values.street_number, values.zipcode, values.city, values.country, values.tour_mappings]);

  const handleManualAddressToggle = () => {
    setIsManualAddress((prev) => !prev);
  };

  useEffect(() => {
    if (isManualAddress) {
      const formattedAddress = `${values.street} ${values.street_number}, ${values.zipcode} ${values.city}, ${values.country}`;
      setFieldValue("formatted_address", formattedAddress);
    }
  }, [values.street, values.street_number, values.zipcode, values.city, values.country, isManualAddress]);

  return (
    <Box height="100%">
      <Box display="flex" mb={4}>
        <Box flex={3}>
          <Typography className={classes._heading} variant="h4">
            {action === "ADD" ? t("New Customer") : t("Edit Customer")}
          </Typography>
        </Box>

        <Box flex={2} textAlign="right" position="relative">
          <Box position="absolute" right={0}>
            <FormCancelSaveButton
              disabled={!isValid || isSubmitting}
              onCancel={closeCustomerHandler}
              onSave={handleSubmit}
              ref={{ cancelRef: cancelButtonRef, saveRef: saveButtonRef }}
            />
          </Box>
        </Box>
      </Box>
      {!!similarCustomers?.length && (
        <Box my={2}>
          <Alert severity="error">
            {t("Similar Customer found")}:&nbsp;
            {similarCustomers.map((c, i) => (
              <>
                <Link to={PATHS.customers.view.replace(":id", c.id)} target="_blank">
                  {c.name}
                </Link>
                , &nbsp;
              </>
            ))}
          </Alert>
        </Box>
      )}
      {action === "EDIT" && isAutoGeoLocation && (

        <Box flex={8} textAlign="center" position="relative">
          <Alert
            severity="error"
          >
            {t("Warning")}:&nbsp;
            {values?.geo_location_status === 'auto'
              ? t("Please verify the customer address and location on the map. If both are correct, save the customer.")
              : values?.geo_location_status === 'error' ? t("Please verify the customer address and location on the map. If both are correct, save the customer.") : null}
          </Alert>
        </Box>
      )}
      <Typography className={classes._subheading} variant="h5">
        {t("Basic Data")}
      </Typography>
      <Grid container spacing={2}>
        {isAdminUser && (
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Autocomplete
              onBlur={handleBlur}
              name="supplier_id"
              label="Supplier"
              errors={errors}
              value={values.supplier_id}
              settings={{
                disableClearable: true,
                valueProp: "id",
                labelProp: "name",
              }}
              onChange={(selected) => {
                setFieldValue("supplier_id", selected.id);
                applyAutomaticTourFromPostal(values.zipcode, selected.id);
              }}
              options={suppliers}
            />
          </Grid>
        )}
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4} lg={4}>
          <Input
            label={t("Name")}
            name="name"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.name}
            errors={errors}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <Input
            label={t("Number")}
            name="number"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.number}
            errors={errors}
            required
          />
        </Grid>
      </Grid>
      {(user?.permissions?.customers?.show_coordinates || user?.permissions?.customers?.allow_manual_customers) && (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Input
              label={t("Latitude")}
              name="latitude"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.latitude}
              errors={errors}
              disabled={!isManualAddress}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Input
              label={t("Longitude")}
              name="longitude"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.longitude}
              errors={errors}
              disabled={!isManualAddress}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            {user?.permissions?.customers?.allow_manual_customers && (
              <Button
                variant="outlined"
                color="primary"
                style={{ marginTop: "16px" }}
                onClick={handleManualAddressToggle}
              >
                {isManualAddress ? t("Disable manual address") : t("Manually add address")}
              </Button>
            )}
          </Grid>
        </Grid>
      )}
      {user?.permissions?.customers?.allow_manual_customers && (
        <div style={{ display: user?.permissions?.customers?.allow_manual_customers ? "block" : "none" }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Input
                label={t("Street")}
                name="street"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.street}
                errors={errors}
                required
                disabled={!isManualAddress}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Input
                label={t("House No.")}
                name="street_number"
                onChange={handleChange}
                onBlur={(e) => {
                  checkDuplicated();
                }}
                value={values.street_number}
                errors={errors}
                required
                disabled={!isManualAddress}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={2} lg={1}>
              <Input
                label={t("Zipcode")}
                name="zipcode"
                onChange={(e) => {
                  const { value } = e.target;
                  setFieldValue("zipcode", value);
                  applyAutomaticTourFromPostal(value);
                }}
                onBlur={(e) => {
                  const value = e.target.value || "";
                  applyAutomaticTourFromPostal(value);
                  checkDuplicated();
                }}
                value={values.zipcode}
                errors={errors}
                required
                disabled={!isManualAddress}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Input
                label={t("City")}
                name="city"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.city}
                errors={errors}
                required
                disabled={!isManualAddress}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Autocomplete
                onBlur={handleBlur}
                name="country"
                label="Country"
                errors={errors}
                value={values.country}
                settings={{
                  disableClearable: true,
                  valueProp: "code",
                  labelProp: "name",
                }}
                onChange={(selected) => {
                  setFieldValue("country", selected.code);
                }}
                options={countries.map(({ code, name }) => ({
                  code,
                  name: i18n.language === "en" ? name : t(`country.${code}`),
                }))}
                required
                disabled={!isManualAddress}
              />
            </Grid>
          </Grid>
        </div>
      )}
      {!user?.permissions?.customersHideLocationRelatedFields && !!user?.permissions?.customers?.show_location_fields && (
        <>
          <Grid container spacing={2}>
            {user?.permissions?.customers?.show_received_location_and_status && action !== "ADD" && (
              <Grid item xs={12} sm={12} md={8} lg={6}>
                <Input
                  label={t("Received Address")}
                  name="received_address"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.received_address}
                  errors={errors}
                  disabled
                  required
                />
              </Grid>)}
            <Grid item xs={12}>
              {errors.address && <Alert severity="error">{errors.address}</Alert>}
              <SelectGeoCoordinates
                onChange={({
                  latitude,
                  longitude,
                  street,
                  street_number,
                  zipcode,
                  city,
                  country,
                  formatted_address,
                }) => {
                  const fields = {
                    latitude,
                    longitude,
                    street,
                    street_number,
                    zipcode,
                    city,
                    country,
                    formatted_address,
                  };

                  Object.entries(fields).forEach(([key, value]) => {
                    if (value) {
                      setFieldValue(key, value);
                    }
                  });

                  // Set zipcode
                  customHandleChange({
                    target: {
                      name: "zipcode",
                      value: zipcode || values.zipcode,
                    },
                  });
                }}
                latitude={values.latitude || 52.52321191756548}
                longitude={values.longitude || 13.405897492100648}
                initialInputValue={
                  values.id
                    ? values.formatted_address
                      ? values.formatted_address
                      : `${values.street} ${values.street_number}, ${values.zipcode}, ${values.city}, ${values.country}`
                    : ""
                }
                disabled={isManualAddress}
              />
            </Grid>
          </Grid>
        </>
      )
      }
      {
        showLocationFields && (
          <>
            <Typography className={classes._subheading} variant="h5">
              {t("Contact")}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Select
                  label={t("Salutation")}
                  name="contact_salutation"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.contact_salutation}
                  errors={errors}
                  options={["Mr", "Ms", "Dr"].map((o) => ({
                    label: t(o),
                    value: o,
                  }))}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={2}>
                <Input
                  label={t("First Name")}
                  name="contact_name"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.contact_name}
                  errors={errors}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Input
                  label={t("Last Name")}
                  name="contact_surname"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.contact_surname}
                  errors={errors}
                />
              </Grid>
            </Grid>
          </>
        )
      }
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <Input
            label={t("Phone")}
            name="phone"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.phone}
            errors={errors}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Input
            label={t("E-Mail")}
            type="email"
            name="email"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.email}
            errors={errors}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <Input
            label={t("Remarks")}
            name="default_remarks"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.default_remarks}
            errors={errors}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <br />
          <div ref={!isAdminUser ? lastElementRef : null}>
            <Checkbox
              checked={values.email_notifications}
              value={values.email_notifications}
              name="email_notifications"
              onChange={handleChange}
              onBlur={handleBlur}
              errors={errors}
              style={{ color: "#6F9CEB" }}
            />
            <Typography component="span" className="font-size-12">
              {t("Notify when Tour starts")}
            </Typography>
          </div>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box mb={2}>
            <Typography className={classes._subheading} variant="h5" style={{ marginTop: "16px", marginBottom: "8px" }}>
              {t("Tour")}
            </Typography>
          </Box>
          {!!deliveryTours.length && (
            <>
              {(values.tour_mappings || []).map((mapping, index) => {
                const selectedTour = deliveryTours.find((t) => Number(t.id) === Number(mapping.tour_id));
                const allDepartures = selectedTour
                  ? (selectedTour.delivery_timing_configs || [])
                    .map((config) => config.delivery_timing)
                    .filter(Boolean)
                  : [];
                const otherSelectedDepartures = (values.tour_mappings || [])
                  .filter((_, i) => i !== index)
                  .flatMap((m) => m.departures || []);
                const departures = allDepartures.filter((d) => !otherSelectedDepartures.includes(d));

                const filteredTours = deliveryTours.filter((o) => {
                  const tourDepartures = (o.delivery_timing_configs || [])
                    .map((config) => config.delivery_timing)
                    .filter(Boolean);
                  const hasAvailableDeps = tourDepartures.some((d) => !otherSelectedDepartures.includes(d));
                  return hasAvailableDeps || Number(o.id) === Number(mapping.tour_id);
                });

                const rowToursOptions = filteredTours.map((o) => ({
                  label: o.name,
                  value: o.id,
                  disabled: !o.active,
                }));

                return (
                  <Grid container spacing={2} key={index} alignItems="center" style={{ marginBottom: "12px" }}>
                    <Grid item xs={12} sm={5} md={4} lg={3}>
                      <MUIAutocomplete
                        id={`tour-autocomplete-${index}`}
                        options={rowToursOptions}
                        getOptionLabel={(option) => option.label}
                        style={{ width: "100%" }}
                        disabled={
                          (!user?.permissions?.tours?.create && !user?.permissions?.zipcodes?.list) ||
                          values.tour_automatic_assigned
                        }
                        value={rowToursOptions.find((t) => Number(t.value) === Number(mapping.tour_id)) || null}
                        onChange={(evt, opt) => {
                          const newMappings = [...values.tour_mappings];
                          newMappings[index] = {
                            tour_id: opt ? opt.value : null,
                            departures: [],
                          };
                          setFieldValue("tour_mappings", newMappings);
                        }}
                        noOptionsText={t("No options")}
                        disableClearable
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={t("Tour")}
                            required
                            variant="filled"
                            margin="dense"
                            error={!!(errors.tour_mappings?.[index]?.tour_id)}
                            helperText={
                              errors.tour_mappings?.[index]?.tour_id
                                ? t(errors.tour_mappings[index].tour_id)
                                : undefined
                            }
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={5} md={4} lg={3}>
                      <CustomMultiSelect
                        values={departures}
                        errors={errors}
                        onChange={(e) => {
                          const newMappings = [...values.tour_mappings];
                          newMappings[index].departures = e.target.value;
                          setFieldValue("tour_mappings", newMappings);
                        }}
                        setValues={mapping.departures || []}
                        label={t("Departures")}
                        labelId={`tour-departures-label-${index}`}
                        backgroundColor="#dfdfdf"
                        borderColor="#6F9CEB"
                        required
                        disabled={
                          !mapping.tour_id ||
                          (!user?.permissions?.tours?.create && !user?.permissions?.zipcodes?.list) ||
                          values.tour_automatic_assigned
                        }
                        name={`tour_mappings[${index}].departures`}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2} md={2}>
                      <IconButton
                        disabled={
                          values.tour_mappings.length <= 1 ||
                          (!user?.permissions?.tours?.create && !user?.permissions?.zipcodes?.list) ||
                          values.tour_automatic_assigned
                        }
                        onClick={() => {
                          const newMappings = values.tour_mappings.filter((_, i) => i !== index);
                          setFieldValue("tour_mappings", newMappings);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                );
              })}
              {!values.tour_automatic_assigned && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    setFieldValue("tour_mappings", [
                      ...values.tour_mappings,
                      { tour_id: null, departures: [] },
                    ]);
                  }}
                  style={{ marginTop: "8px", marginBottom: "16px" }}
                >
                  {t("Add Tour")}
                </Button>
              )}
            </>
          )}
        </Grid>
        {!!usePermission("customers.show_priority_customer") && (
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <div ref={!isAdminUser ? lastElementRef : null}>
              <Select
                label={t("Express Customer")}
                name="priority"
                onChange={(e) => {
                  const value = e.target.value === "true";
                  setFieldValue("priority", value);
                }}
                onBlur={handleBlur}
                value={values.priority ? "true" : "false"}
                errors={errors}
                options={[
                  { value: "true", label: t("Yes") },
                  { value: "false", label: t("No") },
                ]}
              />
            </div>
          </Grid>
        )}
        {isAdminUser && (
          <Grid item xs={12} sm={6} md={4} lg={4}>
            <div ref={lastElementRef}>
              <Checkbox
                checked={values.tour_automatic_assigned}
                value={values.tour_automatic_assigned}
                name="tour_automatic_assigned"
                onChange={handleChange}
                onBlur={handleBlur}
                errors={errors}
                style={{ color: "#6F9CEB" }}
              />
              <Typography component="span" className="font-size-12">
                {t("Automatic Tour")}
              </Typography>
            </div>
          </Grid>
        )}
      </Grid>
      {
        usePermission("customers.deposit_agreement") && (
          <>
            <Box mb={2}>
              <Typography className={classes._subheading} variant="h5" style={{ marginTop: "24px", marginBottom: "8px" }}>
                {t("Deposit agreement")}
              </Typography>
            </Box>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Input
                  label={t("Deposit agreement")}
                  name="deposit_agreement"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.deposit_agreement}
                  errors={errors}
                  placeholder={t("e.g. Back-door code 1234, basement, neighbor...")}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={8} lg={7}>
                <FormControl fullWidth error={Boolean(fileError)}>
                  <Box
                    display="flex"
                    flexWrap="wrap"
                    alignItems="center"
                    style={{ gap: "8px", minHeight: "52px", marginTop: "8px", marginBottom: "4px" }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      style={{
                        height: "52px",
                        padding: "0 16px",
                        border: "1px dashed #c4c4c4",
                        borderRadius: "4px",
                        cursor: "pointer",
                        textAlign: "center",
                        backgroundColor: "#f5f5f5",
                        boxSizing: "border-box",
                      }}
                    >
                      <input
                        type="file"
                        id="deposit-agreement-files-input"
                        multiple
                        onChange={handleDepositAgreementFileChange}
                        accept="application/pdf,image/*"
                        style={{ display: "none" }}
                      />
                      <label htmlFor="deposit-agreement-files-input" style={{ cursor: "pointer", color: "#000", margin: 0 }}>
                        <Typography variant="body2" color="textSecondary" style={{ display: "flex", alignItems: "center" }}>
                          {t("Upload Document (PDF / Image)")}
                        </Typography>
                      </label>
                    </Box>

                    {/* Existing Files */}
                    {existingDepositFiles.map((file, idx) => (
                      <Box
                        key={`existing-${idx}`}
                        display="flex"
                        alignItems="center"
                        style={{
                          height: "52px",
                          backgroundColor: "#f0f4f8",
                          padding: "0 10px",
                          borderRadius: "4px",
                          maxWidth: "240px",
                          boxSizing: "border-box",
                        }}
                      >
                        <a
                          href={file.url || file.fileUrl || "#"}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            color: "#1976d2",
                            marginRight: "4px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            fontSize: "13px",
                            textDecoration: "underline",
                          }}
                          title={file.original_filename || file.originalFilename || file.storage_filename || file.storageFilename}
                        >
                          {file.original_filename || file.originalFilename || file.storage_filename || file.storageFilename || `${t("Document")} ${idx + 1}`}
                        </a>
                        <IconButton size="small" onClick={() => handleRemoveExistingFile(idx)} aria-label="delete file">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}

                    {/* New Files */}
                    {newDepositFiles.map((file, idx) => (
                      <Box
                        key={`new-${idx}`}
                        display="flex"
                        alignItems="center"
                        style={{
                          height: "52px",
                          backgroundColor: "#e8f5e9",
                          padding: "0 10px",
                          borderRadius: "4px",
                          maxWidth: "240px",
                          boxSizing: "border-box",
                        }}
                      >
                        <a
                          href={URL.createObjectURL(file)}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            color: "#2e7d32",
                            marginRight: "4px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            fontSize: "13px",
                            textDecoration: "underline",
                          }}
                          title={file.name}
                        >
                          {file.name}
                        </a>
                        <IconButton size="small" onClick={() => handleRemoveNewFile(idx)} aria-label="delete file">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                  {fileError && <FormHelperText style={{ color: "red" }}>{fileError}</FormHelperText>}
                </FormControl>
              </Grid>
            </Grid>
          </>
        )
      }
      <OpeningHoursEditor
        value={values.opening_hours}
        onChange={(val) => setFieldValue("opening_hours", val)}
      />
      {
        usePermission("customers.deactivate") && (
          <Typography className={classes._subheading} variant="h5">
            {t("Status")}
          </Typography>
        )
      }
      <Grid container spacing={2}>
        {usePermission("customers.deactivate") && (
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Checkbox
              checked={values.active}
              value={values.active}
              name="active"
              onChange={handleChange}
              onBlur={handleBlur}
              errors={errors}
              style={{ color: "#6F9CEB" }}
            />
            <Typography component="span" className="font-size-12">
              {t("Active")}
            </Typography>
          </Grid>
        )}
        {/* Duplicate marked customers are being treated as Archive */}
        {usePermission("customers.archive") && (
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Checkbox
              checked={values.duplicate}
              name="duplicate"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.priority ? "true" : "false"}
              errors={errors}
              options={[
                { value: "true", label: t("Yes") },
                { value: "false", label: t("No") },
              ]}
            />
            <Typography component="span" className="font-size-12">
              {t("Archive")}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Box >
  );
};
export default CustomerForm;
